import { Injectable, OnDestroy } from '@angular/core';
import { Router, NavigationEnd, ActivatedRouteSnapshot } from '@angular/router';
import { filter } from 'rxjs/operators';
import { StorageService } from '@services/storage/storage.service';
import { GtmEventData, GtmRouteConfig } from './gtm-dto';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { Util } from '@shared/common/utils/util';
import { Auth } from '@angular/fire/auth';

declare global {
  interface Window { dataLayer: any[]; }
}

@Injectable({
  providedIn: 'root'
})
export class GtmService implements OnDestroy {
  private currentStepConfig: GtmRouteConfig | null = null;
  private lastViewKey: string | null = null;
  private lastViewTimestamp = 0;
  private readonly dedupeWindowMs = 1500;
  private userIdCache?: string;
  private userIdRequest: Promise<string | undefined> | null = null;
  private userIdResolved = false;

  // Variables para el cálculo de tiempo
  private stepEntryTime = 0;
  private accumulatedTime = 0;
  private isPageVisible = true;

  // Referencias a handlers para poder remover los event listeners en ngOnDestroy
  private readonly visibilityHandler = () => this.handleVisibilityChange();
  private readonly unloadHandler = () => this.handleUnload();

  constructor(
    private readonly router: Router,
    private readonly storageService: StorageService,
    private readonly http: HttpClient,
    private readonly util: Util,
    private readonly firebaseAuth: Auth,
  ) {
    this.ensureDataLayer();
    this.initVisibilityTracker();
    this.initRouteTracker();
    this.initUnloadTracker();
  }

  public async uid(): Promise<string> {
    try {
      const user = await this.util.getUser();
      if (user) {
        const currentUser: any = await this.firebaseAuth.currentUser;
        return currentUser.uid;
      }
      return "";
    } catch {
      return "";
    }
  }
  private ensureDataLayer(): void {
    window.dataLayer = window.dataLayer || [];
  }

  private async getUserIdFromStorage(): Promise<string | undefined> {
    if (this.userIdResolved) {
      return this.userIdCache;
    }

    if (this.userIdRequest) {
      return this.userIdRequest;
    }

    this.userIdRequest = (async () => {
      try {
        const rawData = await this.storageService.get('motorHiperPersonalizacionData');

        if (!rawData) {
          this.userIdCache = await this.obtenerRutService().then((result) => result?.rut);
          return this.userIdCache;
        }

        const parsedData = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
        const rut = parsedData?.profile?.rut;

        if (rut) {
          this.userIdCache = String(rut);
          return this.userIdCache;
        }

        this.userIdCache = await this.obtenerRutService().then((result) => result?.rut);
        return this.userIdCache;
      } catch (error) {

        try {
          const result = await this.obtenerRutService();
          this.userIdCache = result?.rut ? String(result.rut) : undefined;
          return this.userIdCache;
        } catch (fallbackError) {
          this.userIdCache = undefined;
          return undefined;
        }
      } finally {
        this.userIdResolved = true;
        this.userIdRequest = null;
      }
    })();

    return this.userIdRequest;
  }

  private async obtenerRutService(): Promise<any> {
    const rut = await this.uid();
    const url = `${environment.BASE_BACKEND_NUEVA_WEB}clientes/${rut}/encriptarRutTGM`;
    return this.http.get(url).toPromise();
  }

  public async pushEvent(data: GtmEventData): Promise<void> {
    this.ensureDataLayer();

    // Si el userId ya fue resuelto previamente en memoria, asignarlo síncronamente
    if (this.userIdResolved && this.userIdCache) {
      data.user_id = this.userIdCache;
    } else if (!this.userIdResolved) {
      // Solo esperar de forma asíncrona si aún no se ha resuelto
      const userId = await this.getUserIdFromStorage();
      if (userId) {
        data.user_id = userId;
      }
    }

    window.dataLayer.push(data);
  }

  public trackClick(elemento: string, texto: string, tipo: string, destino: string = '-', valor: string = '-'): void {
    if (!this.currentStepConfig) return;

    void this.pushEvent({
      event: this.currentStepConfig.event_name,
      paso_numero: this.currentStepConfig.paso_numero,
      paso_nombre: this.currentStepConfig.paso_nombre,
      accion: 'click',
      elemento,
      elemento_texto: texto,
      elemento_tipo: tipo,
      destino,
      elemento_valor: valor,
    });
  }

  public trackSameTabNavigation(
    elemento: string,
    texto: string,
    tipo: string,
    destino: string,
    delayMs = 250
  ): void {
    if (!this.currentStepConfig) return;

    void this.pushEvent({
      event: this.currentStepConfig.event_name,
      paso_numero: this.currentStepConfig.paso_numero,
      paso_nombre: this.currentStepConfig.paso_nombre,
      accion: 'click',
      elemento,
      elemento_texto: texto,
      elemento_tipo: tipo,
      destino,
    });

    setTimeout(() => {
      window.location.href = destino;
    }, delayMs);
  }

  public trackNewTabNavigation(elemento: string, texto: string, tipo: string, destino: string): void {
    if (!this.currentStepConfig) return;

    void this.pushEvent({
      event: this.currentStepConfig.event_name,
      paso_numero: this.currentStepConfig.paso_numero,
      paso_nombre: this.currentStepConfig.paso_nombre,
      accion: 'click',
      elemento,
      elemento_texto: texto,
      elemento_tipo: tipo,
      destino,
    });
  }

  private initRouteTracker(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(event => this.processCurrentRoute((event as NavigationEnd).urlAfterRedirects));

    if (this.router.navigated) {
      this.processCurrentRoute();
    }
  }

  private processCurrentRoute(url: string = this.router.url): void {
    if (!/^\/pensions(?:\/|[?#]|$)/.test(url)) {
      if (this.currentStepConfig) {
        this.sendTimeEvent(url);
        this.currentStepConfig = null;
        this.lastViewKey = null;
        this.lastViewTimestamp = 0;
        this.accumulatedTime = 0;
        this.stepEntryTime = 0;
      }
      return;
    }

    const gtmData = this.getGtmDataFromRoute(this.router.routerState.snapshot.root);

    if (gtmData) {
      this.handleStepTransition(gtmData);
    }
  }

  private getGtmDataFromRoute(route: ActivatedRouteSnapshot): GtmRouteConfig | null {
    if (route.data?.['gtm']) {
      return route.data['gtm'] as GtmRouteConfig;
    }

    for (const child of route.children) {
      const found = this.getGtmDataFromRoute(child);
      if (found) return found;
    }

    return null;
  }

  public handleStepTransition(newConfig: GtmRouteConfig): void {
    const stepKey = `${newConfig.paso_numero}:${this.router.url}`;
    const now = performance.now();

    const isDifferentStep =
      !this.currentStepConfig ||
      this.currentStepConfig.paso_numero !== newConfig.paso_numero;

    const isNewView =
      this.lastViewKey !== stepKey ||
      now - this.lastViewTimestamp > this.dedupeWindowMs;

    // Salida del paso anterior
    if (this.currentStepConfig && isDifferentStep) {
      this.sendTimeEvent();
    }

    this.currentStepConfig = newConfig;

    // Solo iniciar nueva sesión si realmente habrá nuevo view
    if (isNewView) {
      this.lastViewKey = stepKey;
      this.lastViewTimestamp = now;

      this.resetTimer();

      void this.pushEvent({
        event: this.currentStepConfig.event_name,
        paso_numero: this.currentStepConfig.paso_numero,
        paso_nombre: this.currentStepConfig.paso_nombre,
        accion: 'view',
        elemento: 'Vista de página',
        elemento_texto: 'Vista de página',
        elemento_tipo: '-',
        destino: this.router.url
      });
    }
  }

  private initVisibilityTracker(): void {
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  private handleVisibilityChange(): void {
    const now = performance.now();

    if (document.visibilityState === 'hidden') {
      if (this.isPageVisible && this.currentStepConfig) {
        this.accumulatedTime += now - this.stepEntryTime;
      }
      this.isPageVisible = false;
      return;
    }

    this.stepEntryTime = now;
    this.isPageVisible = true;
  }

  private initUnloadTracker(): void {
    // Escuchamos pagehide que es más fiable que beforeunload en navegadores modernos y móviles
    window.addEventListener('pagehide', this.unloadHandler);
  }

  private handleUnload(): void {
    if (!this.currentStepConfig) return;

    // Generar el payload del tiempo acumulado hasta el momento del cierre
    const payload = this.buildTimePayload();

    // Adjuntar el user_id de forma síncrona si ya existe en la caché de memoria
    if (this.userIdCache) {
      payload.user_id = this.userIdCache;
    }

    // PUSH SÍNCRONO DIRECTO (Sin usar async/await ni Promesas)
    this.ensureDataLayer();
    window.dataLayer.push(payload);
    console.log('GTM Time Event on Unload (Sync Pushed):', payload);
  }

  private resetTimer(): void {
    this.accumulatedTime = 0;
    this.stepEntryTime = performance.now();
    this.isPageVisible = document.visibilityState === 'visible';
  }

  private buildTimePayload(destino: string = this.router.url): GtmEventData {
    if (!this.currentStepConfig) {
      throw new Error('No hay currentStepConfig para generar evento time');
    }

    let finalTime = this.accumulatedTime;
    if (this.isPageVisible) {
      finalTime += performance.now() - this.stepEntryTime;
    }

    return {
      event: this.currentStepConfig.event_name,
      paso_numero: this.currentStepConfig.paso_numero,
      paso_nombre: this.currentStepConfig.paso_nombre,
      accion: 'time',
      elemento: 'Salida del paso (avance/abandono)',
      elemento_texto: 'Salida del paso (avance/abandono)',
      elemento_tipo: '-',
      destino,
      tiempo_segundos: Math.max(0, Math.round(finalTime / 1000)),
    };
  }

  private sendTimeEvent(destino: string = this.router.url): void {
    if (!this.currentStepConfig) return;
    void this.pushEvent(this.buildTimePayload(destino)).catch((error) => {
      console.error('Error al enviar evento GTM time', error);
    });
  }

  public trackGenericEvent(
    accion: string,
    elemento: string,
    texto: string,
    tipo: string,
    destino: string = '-',
    motivo: string = '-',
  ): void {
    if (!this.currentStepConfig) return;

    void this.pushEvent({
      event: this.currentStepConfig.event_name,
      paso_numero: this.currentStepConfig.paso_numero,
      paso_nombre: this.currentStepConfig.paso_nombre,
      accion,
      elemento,
      elemento_texto: texto,
      elemento_tipo: tipo,
      destino,
      motivo,
    }).catch((error) => {
      console.error('Error al enviar evento GTM', error);
    });
  }

  ngOnDestroy(): void {
    document.removeEventListener('visibilitychange', this.visibilityHandler);
    window.removeEventListener('pagehide', this.unloadHandler);
  }
}
